import ReferencePriceTooltipContent from 'src/components/Tooltip/DefinitionTooltip/content/components/ReferencePriceTooltipContent';
import { DefinitionTooltipConfig } from 'src/components/Tooltip/DefinitionTooltip/types';
import Link from 'src/components/primitives/Link/Link';
import { QUOTE_ASSET_SYMBOL, VELOCITY_DOCS_URL } from 'src/constants/constants';

const tradingTooltips = {
	tradingCrossMargin: {
		title: 'Cross Margin',
		body: 'Risk-adjusted USD value of collateral available for new positions.',
	},
	tradingIsolatedMargin: {
		title: 'Isolated Margin',
		body: `Available ${QUOTE_ASSET_SYMBOL} for new positions - only ${QUOTE_ASSET_SYMBOL} balance counts in isolated margin.`,
	},
	tradingMaintenanceMargin: {
		title: 'Maintenance Margin',
		body: 'The minimum required to maintain your positions. Below that, liquidators can liquidate any portion of assets and liabilities until your account is above the minimum.',
	},
	tradingInitialMargin: {
		title: 'Initial Margin',
		body: 'The minimum required to open a new position.',
	},
	tradingAccountHealth: {
		title: 'Account Health %',
		body: 'Your account health percentage is equal to 1 - Maintenance Margin / Maintenance Collateral.',
	},
	tradingNetUsdValue: {
		title: 'Net USD Value',
		body: 'The total USD value of your deposits, borrows, and unsettled.',
	},
	tradingTotalCollateral: {
		title: 'Total Collateral',
		body: 'Risk-adjusted USD value of collateral minus unrealized loss. Your positions will be liquidated once Total Collateral ≤ Maintenance Margin.',
	},
	tradingFreeCollateral: {
		title: 'Free Collateral',
		body: 'The USD value of collateral that can be used to open new positions.',
	},
	tradingReduceOnly: {
		title: 'Reduce Only',
		body: 'The order will never increase nor flip the current position.',
	},
	tradingPostOnly: {
		title: 'Post Only',
		body: (
			<>
				<p>
					The order is a maker order that can provide liquidity to the pool for
					a rebate.
				</p>
				<p>
					Without this flag, orders that don&apos;t execute immediately are not
					post only.
				</p>
			</>
		),
	},
	tradingOrderCount: {
		title: 'Order Count',
		body: 'Your account can have up to 32 open orders.',
	},
	tradingScaledOrderSizeDistribution: {
		title: 'Size Distribution',
		body: (
			<div>
				Distribution method terminology:
				<ul className="list-inside list-disc pl-sm">
					<li>Flat: All orders will have the same size.</li>
					<li>Ascending: Order sizes will linearly increase.</li>
					<li>Descending: Order sizes will linearly decrease.</li>
				</ul>
			</div>
		),
	},
	tradingMarkPrice: {
		title: 'Mark Price',
		body: 'The average of the bid/ask price.',
	},
	tradingBreakEvenPrice: {
		title: 'Break Even Price',
		body: 'The price at which your position can be closed to cover all acquisition and holding costs with no net loss.',
	},
	tradingDirectRouteSwap: {
		title: 'Direct Route Swap',
		body: 'A direct route swap is more likely to succeed but often incurs a higher price impact.',
	},
	tradingOrderTickSize: {
		title: 'Order Tick Size',
		body: 'The smallest unit of change for the price of an order.',
	},
	tradingOrderStepSize: {
		title: 'Order Step Size',
		body: 'The smallest unit of change for the size of an order.',
	},
	tradingLimitedInsuranceFund: {
		title: 'Limited Insurance Fund',
		body: (
			<>
				<p>
					This market uses a limited insurance fund (fee pool only) to protect
					against levered losses.
				</p>
				<Link
					href={`${VELOCITY_DOCS_URL}/insurance-fund/insurance-fund-intro`}
					openInNewTab
				>
					Learn more
				</Link>
			</>
		),
	},
	tradingReferencePrice: {
		title: 'Reference Price',
		body: <ReferencePriceTooltipContent />,
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const earnTooltips = {
	earnMaintenanceMargin: {
		title: 'Total Collateral',
		body: 'The total maintenance weighted value of your collateral. If the maintenance weighted value of your liabilities exceeds this value, your account can be liquidated.',
	},
	earnInitialMargin: {
		title: 'Total Collateral',
		body: 'The total initial weighted value of your collateral. If the initial weighted value of your liabilities exceeds this value, your account cannot take risk-increasing actions.',
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const portfolioTooltips = {
	portfolioAccountValue: {
		title: 'Account Value',
		body: 'Includes spot asset changes and unsettled P&L.',
	},
	portfolioAccountHealth: {
		title: 'Account Health %',
		body: 'Your account health (0-100%) represents how close you are to being liquidated.',
	},
	portfolioPerformancePnlChange: {
		title: 'P&L Performance',
		body: 'The change in your cumulative P&L for the selected timeframe.',
	},
	portfolioMaxDrawdown: {
		title: 'Max Drawdown',
		body: 'Largest perp trading loss experienced from the peak value to the trough.',
	},
	portfolioSharpeRatio: {
		title: 'Sharpe Ratio',
		body: 'Annualized risk-adjusted return.',
	},
	portfolioNetFunding: {
		title: 'Net Funding',
		body: 'The change in your cumulative net funding for the selected timeframe. Expand to see by each market.',
	},
	portfolioTotalEarningApy: {
		title: 'Total Earning APY',
		body: 'Your weighted earning APY of all deposited assets. This does not account for borrow APY.',
	},
	portfolioTotal90DEarning: {
		title: 'Total 90D Earnings',
		body: 'Your total earned APY and rewards received in the last 90 days across all assets, excluding native yields.',
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const strategyVaultsTooltips = {
	strategyVaultsYourBalance: {
		title: 'Your Balance',
		body: 'Current net value of your deposited assets in all your active positions.',
	},
	strategyVaultsYourEarnings: {
		title: 'Your Earnings',
		body: 'Your total lifetime earnings. Tracks all earnings from Strategy Vaults, even after withdrawals.',
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const insuranceFundVaultsTooltips = {
	insuranceFundVaultsYourBalance: {
		title: 'Your Balance',
		body: `The combination of your stake and yield earned on it. This is the total amount you'll be able to unstake.`,
	},
	insuranceFundVaultsYourEarnings: {
		title: 'Your Earnings',
		body: 'Your total yield earned.',
	},
	insuranceFundVaultsSocializedLoss: {
		title: 'Socialized Loss',
		body: 'The total loss distributed between vAMM reserves and users. vAMM reserves are used first to absorb losses to reduce user impact. This value shows only users’ socialized loss.',
	},
	insuranceFundVaultsYourShare: {
		title: 'Your Share',
		body: `Your share of Vault's yield based on your current balance.`,
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

export const pageTooltips = {
	...tradingTooltips,
	...portfolioTooltips,
	...earnTooltips,
	...strategyVaultsTooltips,
	...insuranceFundVaultsTooltips,
};
